import { useQuery } from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'

const API_URL = import.meta.env.VITE_EPC_API_URL ?? ''

export interface EventInvitationEmailLogProfile {
  profile_id: string
  first_name?: string
  last_name?: string
  avatar_url?: string
  role?: string
}

export interface EventInvitationEmailLogEntry {
  id: string
  event_id: string
  invitation_id?: string | null
  profile_id?: string | null
  email_address?: string | null
  status: 'SENT' | 'FAILED' | 'SKIPPED' | string
  source:
    | 'OPEN_INVITE_SYSTEM'
    | 'EVENT_PUBLISH_INVITE'
    | 'OPEN_INVITE_SYSTEM_DRY_RUN'
    | 'EVENT_PUBLISH_INVITE_DRY_RUN'
    | string
  attempted_at?: string | null
  sent_at?: string | null
  message_id?: string | null
  error_message?: string | null
  triggered_by_profile_id?: string | null
  profile?: EventInvitationEmailLogProfile | null
}

interface PaginationInfo {
  total_count: number
  current_page: number
  total_pages: number
  limit: number
  skip: number
  has_next: boolean
  has_previous: boolean
}

interface StatusCounts {
  SENT?: number
  FAILED?: number
  SKIPPED?: number
  [key: string]: number | undefined
}

interface SourceCounts {
  OPEN_INVITE_SYSTEM?: number
  EVENT_PUBLISH_INVITE?: number
  OPEN_INVITE_SYSTEM_DRY_RUN?: number
  EVENT_PUBLISH_INVITE_DRY_RUN?: number
  [key: string]: number | undefined
}

interface EventInvitationEmailLogsResponse {
  logs: EventInvitationEmailLogEntry[]
  status_counts: StatusCounts
  source_counts: SourceCounts
  filters: {
    status?: string
    source?: string
  }
  pagination: PaginationInfo
}

interface UseEventInvitationEmailLogsOptions {
  limit?: number
  skip?: number
  status?: string
  source?: string
}

interface UseEventInvitationEmailLogsConfig {
  enabled?: boolean
}

export const useEventInvitationEmailLogs = (
  eventId: string,
  options: UseEventInvitationEmailLogsOptions = {},
  config: UseEventInvitationEmailLogsConfig = {}
) => {
  const fetchWithAuth = useFetchWithAuth()
  const { limit = 20, skip = 0, status, source } = options
  const { enabled = true } = config

  return useQuery({
    queryKey: ['eventInvitationEmailLogs', eventId, limit, skip, status, source],
    queryFn: async (): Promise<EventInvitationEmailLogsResponse> => {
      if (!eventId) {
        throw new Error('Event ID is required')
      }

      const params = new URLSearchParams()
      params.append('limit', limit.toString())
      if (skip > 0) {
        params.append('skip', skip.toString())
      }
      if (status) {
        params.append('status', status)
      }
      if (source) {
        params.append('source', source)
      }

      const response = await fetchWithAuth(
        `${API_URL}/events/${eventId}/invitation-email-logs?${params.toString()}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      )

      return response
    },
    enabled: enabled && !!eventId,
    staleTime: 60 * 1000,
  })
}
